"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Wrench, ExternalLink, ArrowLeft, Layers, Zap, Eye, Shield,
  ChevronDown, ChevronUp, Info, AlertTriangle, CheckCircle, Star
} from "lucide-react";

function buildAmazonSearchUrl(query: string): string {
  const encoded = encodeURIComponent(query);
  return `https://www.amazon.co.jp/s?k=${encoded}&tag=netage05-22`;
}


interface Tool {
  name: string;
  description: string;
  uses: string[];
  essential: boolean;
}

interface SelectionTip {
  label: string;
  text: string;
  type: "recommend" | "caution" | "info";
}

interface ToolCategory {
  icon: React.ElementType;
  title: string;
  color: string;
  borderColor: string;
  bgColor: string;
  guideBorderColor: string;
  guideBgColor: string;
  guideTitle: string;
  guideBody: string;
  selectionTips: SelectionTip[];
  tools: Tool[];
}

const toolCategories: ToolCategory[] = [
  {
    icon: Wrench,
    title: "ドライバー類",
    color: "text-blue-400",
    borderColor: "border-blue-500/30",
    bgColor: "bg-blue-500/5",
    guideBorderColor: "border-blue-500/20",
    guideBgColor: "bg-blue-500/5",
    guideTitle: "ドライバーの選び方",
    guideBody: "スマートフォン修理では、一般的なドライバーではなく精密ドライバーが必要です。iPhoneはペンタローブ（星型5角）という特殊規格を採用しており、専用ドライバーなしでは外装ネジを外せません。AndroidはメーカーによってプラスやトルクスT3〜T5など規格が異なります。修理する機種が決まっている場合は個別購入でも十分ですが、複数機種を扱う予定なら多ビットの精密ドライバーセットが経済的です。ドライバーは安価な製品だとネジ頭を舐めやすいため、iFixitやWiHAなど信頼性の高いブランドを選ぶことを推奨します。",
    selectionTips: [
      { label: "iPhoneのみ修理する場合", text: "ペンタローブP2（外装）＋プラス#000（内部）の2本で大半の作業をカバーできます。", type: "recommend" },
      { label: "Android複数機種を修理する場合", text: "プラス#000/#00＋トルクスT3/T4/T5を含む精密ドライバーセットが最適です。", type: "recommend" },
      { label: "安価なセットに注意", text: "100円ショップや格安セットはビットが滑りやすく、ネジ頭を破損させるリスクがあります。修理工具は品質優先で選びましょう。", type: "caution" },
      { label: "磁気ビットが便利", text: "磁気付きのビットはネジを落とさずに扱えるため、狭い内部での作業が格段に楽になります。", type: "info" },
    ],
    tools: [
      { name: "ペンタローブドライバー（P2）", description: "iPhone修理に必須の星型ドライバー。iPhoneの外装ネジに使用。", uses: ["iPhone 4以降の外装ネジ"], essential: true },
      { name: "ペンタローブドライバー（P5）", description: "MacBook等に使用される大型ペンタローブ。", uses: ["MacBook、一部のAndroid端末"], essential: false },
      { name: "プラスドライバー（#000）", description: "精密プラスドライバー。スマートフォン内部の小型ネジに使用。", uses: ["内部基板固定ネジ", "バッテリーブラケット"], essential: true },
      { name: "プラスドライバー（#00）", description: "やや大きめの精密プラスドライバー。", uses: ["内部ネジ全般", "カメラブラケット"], essential: true },
      { name: "トルクスドライバー（T3/T4/T5）", description: "六角星型のトルクスドライバー。Android端末に多く使用。", uses: ["Samsung Galaxy内部ネジ", "Sony Xperia内部ネジ"], essential: false },
      { name: "精密ドライバーセット", description: "各種ビットが揃ったセット。複数機種を修理する場合に便利。", uses: ["各種スマートフォン全般"], essential: true },
    ],
  },
  {
    icon: Layers,
    title: "開封・分解ツール",
    color: "text-orange-400",
    borderColor: "border-orange-500/30",
    bgColor: "bg-orange-500/5",
    guideBorderColor: "border-orange-500/20",
    guideBgColor: "bg-orange-500/5",
    guideTitle: "開封・分解ツールの選び方",
    guideBody: "近年のスマートフォンは接着剤でパネルが固定されており、ネジを外すだけでは分解できません。吸盤で画面を浮かせ、ピックを差し込んで接着剤を切り離す手順が基本です。スパジャーはコネクタの取り外しや内部部品の分離に使う非導電性のヘラで、金属製より安全なプラスチック製を選ぶのが基本です。ピンセットは先端が細く、ESD（静電気放電）対策済みのものが推奨されます。安価なプラスチック製ツールは折れやすいため、適度な硬さと柔軟性を持つナイロン製またはポリカーボネート製を選ぶとよいでしょう。",
    selectionTips: [
      { label: "吸盤の吸着力を確認する", text: "ガラスが割れている場合は吸盤が密着しません。その場合は強力両面テープを貼った上に吸盤を使う方法が有効です。", type: "info" },
      { label: "ピックは複数枚用意する", text: "ピックは消耗品です。1枚では足りないことが多いため、5〜10枚セットの購入を推奨します。", type: "recommend" },
      { label: "金属スパジャーは慎重に", text: "金属製スパジャーは基板や電池に接触すると短絡（ショート）の危険があります。初心者はプラスチック製のみを使用してください。", type: "caution" },
      { label: "ピンセットはESD対応品を", text: "ESD対応でないピンセットは静電気で基板のICチップを破損させる可能性があります。必ずESD対応と明記されたものを選びましょう。", type: "recommend" },
    ],
    tools: [
      { name: "吸盤（スクリーン用）", description: "画面を持ち上げるための吸盤。ガラス面に密着させて使用。", uses: ["画面交換", "バックパネル開封"], essential: true },
      { name: "スパジャー（プラスチック製）", description: "コネクタの取り外しや部品の分離に使用する非導電性ツール。", uses: ["コネクタ取り外し", "フレキシブルケーブル分離"], essential: true },
      { name: "金属スパジャー", description: "より強固な分離作業に使用。ただし基板への接触に注意。", uses: ["強固な接着剤の剥離", "金属部品の分離"], essential: false },
      { name: "ピック（オープニングツール）", description: "薄型のプラスチック製ピック。画面とフレームの間に差し込んで開封。", uses: ["画面開封", "バックパネル開封"], essential: true },
      { name: "ピンセット（先細型）", description: "細かい部品の取り扱いに必須。ESD対応品が望ましい。", uses: ["小型ネジの取り扱い", "フレキシブルケーブルの接続"], essential: true },
      { name: "SIMカードイジェクトピン", description: "SIMトレイを取り出すためのピン。クリップで代用可能。", uses: ["SIMトレイ取り出し"], essential: false },
    ],
  },
  {
    icon: Zap,
    title: "加熱・接着ツール",
    color: "text-red-400",
    borderColor: "border-red-500/30",
    bgColor: "bg-red-500/5",
    guideBorderColor: "border-red-500/20",
    guideBgColor: "bg-red-500/5",
    guideTitle: "加熱・接着ツールの選び方と安全な使い方",
    guideBody: "スマートフォンの画面やバックパネルは強力な接着剤で固定されており、加熱することで接着剤を軟化させて分解しやすくします。ヒートガンは温度調節機能付きを選び、スマートフォンへの使用は60〜80°Cが目安です。過度な加熱はバッテリーの膨張・発火を招くため、一箇所に集中して当て続けないよう注意が必要です。初心者にはiOpener（電子レンジで温める加熱パッド）のほうが温度管理が容易で安全です。バッテリー交換後の固定には機種専用の両面テープを使用し、汎用品は厚みが合わない場合があるため注意してください。",
    selectionTips: [
      { label: "ヒートガンの温度設定", text: "スマートフォンへの使用は60〜80°Cが適切です。温度調節なしの製品は過熱リスクがあるため避けましょう。", type: "recommend" },
      { label: "初心者はiOpenerが安全", text: "電子レンジで温めるiOpenerは温度が一定で扱いやすく、バッテリーへの過熱リスクも低いため初心者に最適です。", type: "recommend" },
      { label: "バッテリー付近の加熱は最小限に", text: "リチウムイオン電池は60°C以上で劣化が加速し、膨張・発火のリスクがあります。バッテリー付近は短時間の加熱にとどめてください。", type: "caution" },
      { label: "接着テープは機種専用品を", text: "バッテリー固定テープは機種ごとにサイズが異なります。汎用品を使うとバッテリーが固定されず、動作不良の原因になります。", type: "info" },
    ],
    tools: [
      { name: "ヒートガン", description: "接着剤を温めて柔らかくするための加熱ツール。温度調節機能付きが推奨。", uses: ["画面接着剤の軟化", "バッテリー接着テープの剥離"], essential: true },
      { name: "iOpener（加熱パッド）", description: "電子レンジで温めて使用する加熱パッド。ヒートガンより安全。", uses: ["画面周囲の加熱", "バックパネルの加熱"], essential: false },
      { name: "両面接着テープ（バッテリー用）", description: "バッテリー固定用の薄型両面テープ。機種ごとに適切なサイズを選ぶ。", uses: ["バッテリー固定"], essential: true },
      { name: "防水テープ・接着剤", description: "防水機種の修理後に使用する専用シール材。", uses: ["防水機種の再シール"], essential: false },
    ],
  },
  {
    icon: Eye,
    title: "作業環境・補助ツール",
    color: "text-green-400",
    borderColor: "border-green-500/30",
    bgColor: "bg-green-500/5",
    guideBorderColor: "border-green-500/20",
    guideBgColor: "bg-green-500/5",
    guideTitle: "作業環境の整え方",
    guideBody: "精密機器の修理では、作業環境の整備が成否を大きく左右します。静電気はスマートフォンの基板にとって大敵であり、ESD防止リストバンドと導電性マットの組み合わせで静電気リスクを大幅に低減できます。ネジの紛失も修理失敗の主な原因の一つです。磁気マットやネジ管理シートを使って取り外したネジを位置ごとに整理する習慣をつけましょう。また、スマートフォン内部の部品は非常に小さいため、十分な照明と必要に応じた拡大鏡の使用が作業精度を高めます。",
    selectionTips: [
      { label: "ESDリストバンドは必ず着用する", text: "乾燥した環境では静電気が発生しやすく、一瞬の放電でICチップが破損します。作業前に必ずリストバンドを着用し、アース接続を確認してください。", type: "recommend" },
      { label: "磁気マットでネジを管理する", text: "スマートフォン内部のネジは直径1mm以下のものもあります。磁気マットに位置ごとに並べておくと、組み立て時に迷わず作業できます。", type: "recommend" },
      { label: "照明は明るすぎるくらいがちょうどよい", text: "暗い環境での作業はコネクタの見落としやケーブルの損傷につながります。LEDライトで作業面を十分に照らしてください。", type: "info" },
      { label: "拡大鏡は10倍以上を推奨", text: "フレキシブルケーブルのコネクタや基板のはんだ状態を確認するには、10倍以上の拡大鏡またはデジタルマイクロスコープが有効です。", type: "info" },
    ],
    tools: [
      { name: "磁気マット（ネジ管理マット）", description: "取り外したネジを整理・管理するためのマット。紛失防止に必須。", uses: ["ネジの整理・管理"], essential: true },
      { name: "ESD防止リストバンド", description: "静電気による基板破損を防ぐためのアースバンド。", uses: ["静電気対策"], essential: true },
      { name: "精密作業マット", description: "ESD対応の作業マット。部品の紛失防止と静電気対策を兼ねる。", uses: ["作業台の保護", "静電気対策"], essential: false },
      { name: "拡大鏡・ルーペ", description: "細かい部品や基板の確認に使用。LEDライト付きが便利。", uses: ["細部の確認", "基板の目視検査"], essential: false },
      { name: "ライト（作業灯）", description: "作業スペースを明るく照らすためのLEDライト。", uses: ["作業照明"], essential: true },
    ],
  },
  {
    icon: Shield,
    title: "清掃・メンテナンスツール",
    color: "text-purple-400",
    borderColor: "border-purple-500/30",
    bgColor: "bg-purple-500/5",
    guideBorderColor: "border-purple-500/20",
    guideBgColor: "bg-purple-500/5",
    guideTitle: "清掃ツールの選び方と使用上の注意",
    guideBody: "修理の前後に適切な清掃を行うことで、接触不良や再修理のリスクを減らせます。無水エタノール（IPA）は接着剤の残留物や基板の汚れを除去するのに最適ですが、純度が低いと水分が残り基板を腐食させる恐れがあります。必ず99%以上の純度のものを使用してください。充電ポートの接触不良は清掃で改善できるケースが多く、修理前に試す価値があります。エアダスターは逆さにして使うと液化ガスが噴出するため、必ず正立させて使用してください。",
    selectionTips: [
      { label: "IPAは99%以上の純度を選ぶ", text: "薬局で販売されているエタノールは水分が含まれており基板清掃には不向きです。電子部品用の無水エタノール（IPA 99%以上）を使用してください。", type: "recommend" },
      { label: "充電不良はまず清掃を試みる", text: "充電できない症状の多くは充電ポート内のゴミが原因です。交換前に精密ブラシやエアダスターで清掃してみましょう。", type: "info" },
      { label: "エアダスターは正立使用", text: "逆さにして使うと液化ガスが噴出し、基板や部品を凍結・破損させます。必ず正立させて使用してください。", type: "caution" },
      { label: "綿棒はリント（繊維くず）に注意", text: "一般的な綿棒は繊維くずが残ることがあります。電子部品の清掃には繊維くずが出にくいフォームスワブ（発泡スポンジ製）が最適です。", type: "info" },
    ],
    tools: [
      { name: "無水エタノール（IPA）", description: "接着剤の残留物や汚れを除去するための溶剤。99%以上の純度が推奨。", uses: ["接着剤の除去", "基板の清掃"], essential: true },
      { name: "精密ブラシ（ESD対応）", description: "基板や細部の埃・汚れを除去するためのブラシ。", uses: ["充電ポートの清掃", "基板の清掃"], essential: false },
      { name: "エアダスター", description: "圧縮空気で埃を吹き飛ばすスプレー缶。", uses: ["内部の埃除去", "充電ポートの清掃"], essential: false },
      { name: "綿棒・クリーニングスワブ", description: "細部の清掃に使用。先端が細いタイプが作業しやすい。", uses: ["細部の清掃", "接着剤の除去"], essential: false },
    ],
  },
];

const tipIconMap = {
  recommend: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/20" },
  caution: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
};

function CategoryGuide({ category }: { category: ToolCategory }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border ${category.guideBorderColor} ${category.guideBgColor} mb-6 overflow-hidden`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:opacity-90 transition-opacity"
      >
        <div className="flex items-center gap-2">
          <Star className={`w-4 h-4 ${category.color} shrink-0`} />
          <span className="text-sm font-semibold">{category.guideTitle}</span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
          : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
          <div className="overflow-hidden">
            <div className="px-5 pb-5 space-y-5">
              <p className="text-sm text-foreground/80 leading-relaxed border-t border-border/30 pt-4">
                {category.guideBody}
              </p>
              <div>
                <p className="text-xs font-mono text-muted-foreground/60 tracking-widest uppercase mb-3">選び方のポイント</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {category.selectionTips.map((tip) => {
                    const cfg = tipIconMap[tip.type];
                    return (
                      <div key={tip.label} className={`flex items-start gap-3 p-3 rounded-lg border ${cfg.border} ${cfg.bg}`}>
                        <cfg.icon className={`w-4 h-4 ${cfg.color} mt-0.5 shrink-0`} />
                        <div>
                          <p className={`text-xs font-semibold ${cfg.color} mb-0.5`}>{tip.label}</p>
                          <p className="text-xs text-foreground/75 leading-relaxed">{tip.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      
    </div>
  );
}

export default function ToolListClient() {
  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <main className="pt-14">
        <section className="relative py-10 sm:py-16 overflow-hidden border-b border-border/40"
          style={{ background: "linear-gradient(135deg, oklch(0.10 0.015 260) 0%, oklch(0.13 0.02 260) 100%)" }}>
          <div className="absolute inset-0 tech-grid-bg opacity-10" />
          <div className="relative container">
            <Link href="/">
              <span className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" /> トップに戻る
              </span>
            </Link>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Wrench className="w-8 h-8 text-primary" />
                <span className="font-mono text-xs text-primary/70 tracking-widest uppercase">Tool List</span>
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                修理工具一覧
              </h1>
              <p className="text-muted-foreground max-w-2xl leading-relaxed">
                スマートフォン修理に必要な工具をカテゴリ別に整理しました。
                各カテゴリには<span className="text-primary font-medium">選び方ガイド</span>を用意しています。
                <span className="text-primary font-medium">必須</span>マークの工具は修理作業に欠かせないものです。
              </p>
              <p className="text-xs text-muted-foreground/50 mt-3 font-mono">
                ※ Amazonアソシエイトとして適格販売から収入を得ています
              </p>
            </div>
          </div>
        </section>

        <section className="py-8 sm:py-12">
          <div className="container space-y-12">
            {toolCategories.map((category, ci) => (
              <div key={category.title}>
                <div className="flex items-center gap-3 mb-5">
                  <div className={`p-2 rounded-lg border ${category.borderColor} ${category.bgColor}`}>
                    <category.icon className={`w-5 h-5 ${category.color}`} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {category.title}
                  </h2>
                </div>
                <CategoryGuide category={category} />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.tools.map((tool, ti) => (
                    <div
                      key={tool.name}
                     
                     
                     
                     
                      className={`rounded-lg border ${category.borderColor} ${category.bgColor} p-4 flex flex-col gap-3`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold leading-snug">{tool.name}</h3>
                        {tool.essential && (
                          <span className="shrink-0 text-xs font-mono px-1.5 py-0.5 rounded border border-primary/40 text-primary bg-primary/10">必須</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{tool.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {tool.uses.map((use) => (
                          <span key={use} className="text-xs px-2 py-0.5 rounded-full border border-border/50 text-muted-foreground/70">{use}</span>
                        ))}
                      </div>
                      <a
                        href={buildAmazonSearchUrl(tool.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`mt-auto inline-flex items-center gap-1.5 text-xs font-medium ${category.color} hover:opacity-80 transition-opacity`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Amazonで検索
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-10 border-t border-border/40">
          <div className="container">
            <div>
              <h2 className="text-lg font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>おすすめ工具セット</h2>
              <div className="grid sm:grid-cols-3 gap-4 max-w-3xl">
                {[
                  { label: "スマートフォン修理キット", query: "スマートフォン修理工具セット" },
                  { label: "iPhone修理工具セット", query: "iPhone修理工具セット" },
                  { label: "精密ドライバーセット", query: "精密ドライバーセット スマートフォン" },
                ].map((set) => (
                  <a
                    key={set.label}
                    href={buildAmazonSearchUrl(set.query)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-2 p-4 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors group"
                  >
                    <span className="text-sm font-medium text-foreground/90">{set.label}</span>
                    <ExternalLink className="w-4 h-4 text-primary shrink-0 group-hover:scale-110 transition-transform" />
                  </a>
                ))}
              </div>
              <p className="text-xs text-muted-foreground/50 mt-4 font-mono">
                ※ Amazonアソシエイトとして適格販売から収入を得ています（ID: netage05-22）
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 border-t border-border/40">
          <div className="container">
            <div>
              <div className="p-6 rounded-xl border border-primary/30 bg-primary/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold mb-1">機種別の必要工具を確認する</h3>
                  <p className="text-sm text-muted-foreground">各機種の詳細ページでは、その機種に特化した必要工具リストと交換部品リストを確認できます。</p>
                </div>
                <Link href="/models">
                  <span className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                    機種一覧を見る <ExternalLink className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
